import { Injectable, inject } from '@angular/core';
import {
  CorePopupService,
  CoreRouterActionGoBack,
  CoreRouterActionNavigateForward,
  CoreRouterActions,
  selectorCoreInstanceInfo
} from '@bepatient/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { Observable, of } from 'rxjs';
import {
  catchError,
  map,
  switchMap,
  take,
  withLatestFrom
} from 'rxjs/operators';
import { LoadingMeasuresErrorPopupComponent } from './components/loading-measures-error-popup/loading-measures-error-popup.component';
import { MyMeasuresApiService } from './my-measures-api.service';
import {
  MyMeasuresActionLoadCategoriesFailed,
  MyMeasuresActionLoadCategoriesSuccess,
  MyMeasuresActionLoadChart,
  MyMeasuresActionLoadChartFailed,
  MyMeasuresActionLoadChartSuccess,
  MyMeasuresActionLoadMeasuresListFailed,
  MyMeasuresActionLoadMeasuresListSuccess,
  MyMeasuresActions,
  MyMeasuresActionTypes
} from './my-measures.actions';
import { selectorMyMeasuresSelectedMeasure } from './my-measures.reducer';

@Injectable()
export class MyMeasuresEffects {
  private action$ = inject(Actions);
  private store$ = inject<Store<any>>(Store);
  private myMeasuresApiService = inject(MyMeasuresApiService);
  private popup = inject(CorePopupService);
  private translate = inject(TranslateService);

  loadChart = createEffect(() =>
    this.action$.pipe(
      ofType(MyMeasuresActionTypes.LOAD_CHART),
      map((action: MyMeasuresActionLoadChart) => action.payload),
      withLatestFrom(
        this.store$.pipe(select(selectorMyMeasuresSelectedMeasure))
      ),
      switchMap(([payload, selectedMeasure]) =>
        this.myMeasuresApiService
          .loadChart(selectedMeasure.href + (payload || ''))
          .pipe(
            map(res => new MyMeasuresActionLoadChartSuccess(res)),
            catchError(() => of(new MyMeasuresActionLoadChartFailed()))
          )
      )
    )
  );

  loadMeasuresList$: Observable<MyMeasuresActions> = createEffect(() =>
    this.action$.pipe(
      ofType(MyMeasuresActionTypes.LOAD_MEASURES_LIST),
      switchMap(() =>
        this.myMeasuresApiService.loadMeasuresList().pipe(
          map(
            measuresList =>
              new MyMeasuresActionLoadMeasuresListSuccess(measuresList)
          ),
          catchError(() => of(new MyMeasuresActionLoadMeasuresListFailed()))
        )
      )
    )
  );

  loadMeasuresFailed$: Observable<CoreRouterActions> = createEffect(() =>
    this.action$.pipe(
      ofType(MyMeasuresActionTypes.LOAD_MEASURES_LIST_FAILED),
      withLatestFrom(this.store$.pipe(select(selectorCoreInstanceInfo))),
      switchMap(
        ([, { appColor: color }]) =>
          new Observable<CoreRouterActions>(subscriber => {
            this.popup
              .create(LoadingMeasuresErrorPopupComponent, {
                inputs: {
                  color,
                  message: this.translate.instant('MODULES.LIST.ERROR'),
                  buttonLabel: this.translate.instant('OK')
                }
              })
              .then(component => {
                component.validated.pipe(take(1)).subscribe(() => {
                  subscriber.next(new CoreRouterActionGoBack());
                  subscriber.complete();
                  this.popup.dismiss();
                });
              });
          })
      )
    )
  );

  loadCategories$: Observable<MyMeasuresActions> = createEffect(() =>
    this.action$.pipe(
      ofType(MyMeasuresActionTypes.LOAD_CATEGORIES),
      switchMap(() =>
        this.myMeasuresApiService.loadCategories().pipe(
          map(
            categories => new MyMeasuresActionLoadCategoriesSuccess(categories)
          ),
          catchError(() => of(new MyMeasuresActionLoadCategoriesFailed()))
        )
      )
    )
  );

  selectedMeasure$: Observable<CoreRouterActions> = createEffect(() =>
    this.action$.pipe(
      ofType(MyMeasuresActionTypes.SET_SELECTED_MEASURE),
      map(
        () =>
          new CoreRouterActionNavigateForward({
            url: 'my-measures-graph-highcharts'
          })
      )
    )
  );
}
